import { API_BASE_URL } from '../config/env';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: string[] = [],
    /**
     * The parsed response body, when there was one.
     *
     * A failure sometimes carries more than a sentence — "this slug belongs to a deleted
     * game, and here is its slug so you can restore it". Without the body a caller can only
     * match on the message text, and messages get reworded.
     */
    public readonly data: unknown = undefined,
    /**
     * A stable identifier for the refusal — "admin.last_manager". Epic 15 §15.3.
     *
     * The client looks it up in the dictionary and falls back to `errors` when it does not
     * know it, so a message this build has no translation for still reads as English rather
     * than as nothing.
     */
    public readonly code: string | null = null,
    /** The values the translated sentence needs: a name, a count. */
    public readonly args: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Access token lives in memory only — the refresh token is an httpOnly cookie. */
let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;
let inFlightRefresh: Promise<boolean> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

/** Exchanges the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshAccessToken(): Promise<boolean> {
  inFlightRefresh ??= (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        accessToken = null;
        return false;
      }

      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export interface RequestOptions extends RequestInit {
  /** Skip the automatic refresh-and-retry on 401 (used by the auth endpoints themselves). */
  skipRetry?: boolean;
}

/**
 * One request, with the token attached and one refresh-and-retry on 401.
 *
 * Split out of apiFetch so that a download can share the same authentication without also
 * inheriting "parse the body as JSON" — a file is not JSON, and a browser navigation
 * (`<a href>`) cannot carry an in-memory token at all.
 */
async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const { skipRetry, ...init } = options;

  // Let the browser set the multipart boundary itself for FormData bodies.
  const isFormData = init.body instanceof FormData;

  const once = () =>
    fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...init.headers,
      },
    });

  let response = await once();

  if (response.status === 401 && !skipRetry) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      response = await once();
    } else {
      onSessionExpired?.();
    }
  }

  return response;
}

/** Throws an ApiError carrying the server's code and message. */
async function raise(response: Response): Promise<never> {
  const body = await readJson(response.clone());
  const errors = extractErrors(body);

  const { code, args } = extractCode(body);

  throw new ApiError(describeFailure(response, errors), response.status, errors, body, code, args);
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (!response.ok) {
    await raise(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Downloads a file the same way every other call is made — through the token.
 *
 * A plain link cannot do this: the access token lives in memory by design, so the browser
 * has nothing to send and the server answers 401. Fetching the bytes and handing them to a
 * temporary object URL is the price of not keeping the token somewhere a link could reach.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await send(path);

  if (!response.ok) {
    await raise(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = nameFromDisposition(response) ?? fallbackName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Without this the blob stays in memory for the life of the document.
    URL.revokeObjectURL(url);
  }
}

/** The server names the file; the caller's name is only a fallback. */
function nameFromDisposition(response: Response): string | null {
  const header = response.headers.get('Content-Disposition');
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

  return match ? decodeURIComponent(match[1]) : null;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // No JSON body — a 204, a proxy error page, a dropped connection.
    return undefined;
  }
}

function extractErrors(body: unknown): string[] {
  if (!body || typeof body !== 'object') {
    return [];
  }

  const problem = body as { errors?: unknown; error?: unknown };

  if (Array.isArray(problem.errors)) {
    return problem.errors as string[];
  }

  // ASP.NET Core ValidationProblemDetails: { errors: { field: [msg] } }
  if (problem.errors && typeof problem.errors === 'object') {
    return Object.values(problem.errors as Record<string, string[]>).flat();
  }

  if (typeof problem.error === 'string') {
    return [problem.error];
  }

  return [];
}

function describeFailure(response: Response, errors: string[]): string {
  return errors.length > 0
    ? errors.join(' ')
    : `Request failed: ${response.status} ${response.statusText}`.trim();
}

/** The refusal's identifier and its values, when the server sent them. */
function extractCode(body: unknown): { code: string | null; args: Record<string, string> } {
  if (!body || typeof body !== 'object') {
    return { code: null, args: {} };
  }

  const problem = body as { code?: unknown; args?: unknown };

  return {
    code: typeof problem.code === 'string' ? problem.code : null,
    args:
      problem.args && typeof problem.args === 'object'
        ? (problem.args as Record<string, string>)
        : {},
  };
}
